import React, { createContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

export const UserContext = createContext({
  user: null,
  saveUser: () => {},
  clearUser: () => {}
});

// Read the persisted user synchronously so the very first render already knows
// who is logged in. Loading it in an effect instead left `user` null for one
// render, which made the route guards in App.jsx redirect a logged-in admin from
// /admin to /login and then to / on every refresh or direct link.
const readStoredUser = () => {
  try {
    let userData = localStorage.getItem('user') || sessionStorage.getItem('user');

    if (!userData) {
      const cookieData = document.cookie
        .split('; ')
        .find(row => row.startsWith('user='));
      if (cookieData) {
        userData = cookieData.split('=')[1];
      }
    }

    return userData ? JSON.parse(userData) : null;
  } catch {
    return undefined; // corrupt data: signal the effect below to clear storage
  }
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(() => readStoredUser() ?? null);

  useEffect(() => {
    loadUserFromStorage();
  }, []);

  const loadUserFromStorage = () => {
    try {
      const storedUser = readStoredUser();
      if (storedUser === undefined) throw new Error('Unreadable user data');
      if (storedUser) ensureDataConsistency(storedUser);
    } catch (error) {
      // console.error('Error loading user data:', error);
      toast.error("Error loading user data");
      clearAllStorage();
    }  finally {
      toast.dismiss();
    }
  };

  const ensureDataConsistency = (userData) => {
    try {
      const userString = JSON.stringify(userData);
      
      // Update all storage methods
      localStorage.setItem('user', userString);
      sessionStorage.setItem('user', userString);
      document.cookie = `user=${userString}; path=/; max-age=86400; samesite=strict`;
      
      // Also ensure tokens are consistent
      const tokens = {
        accessToken: localStorage.getItem('accessToken'),
        refreshToken: localStorage.getItem('refreshToken')
      };
      
      if (tokens.accessToken && tokens.refreshToken) {
        sessionStorage.setItem('accessToken', tokens.accessToken);
        sessionStorage.setItem('refreshToken', tokens.refreshToken);
        document.cookie = `accessToken=${tokens.accessToken}; path=/; max-age=86400; samesite=strict`;
        document.cookie = `refreshToken=${tokens.refreshToken}; path=/; max-age=604800; samesite=strict`;
      }
    } catch (error) {
      // console.error('Error ensuring data consistency:', error);
      toast.error("Error ensuring data consistency");
    }  finally {
      toast.dismiss();
    }
  };

  const saveUser = (userData) => {
    try {
      if (userData) {
        setUser(userData);
        ensureDataConsistency(userData);
      }
    } catch (error) {
      // console.error('Error saving user data:', error);
      toast.error("Error saving user data");
      // clearAllStorage();
    }  finally {
      toast.dismiss();
    }
  };

  const clearAllStorage = () => {
    setUser(null);
    
    // Clear localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    
    // Clear sessionStorage
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    
    // Clear cookies
    document.cookie = 'user=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT';
    document.cookie = 'accessToken=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT';
    document.cookie = 'refreshToken=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT';
  };

  return (
    <UserContext.Provider value={{ user, saveUser, clearUser: clearAllStorage }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = React.useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};